import { readdir, stat } from "node:fs/promises";
import { join } from "node:path";

import { RepoManager } from "../repo/repo-manager";
import {
    importJsConfigModule,
    pathExists,
    resolveFirstExistingPath,
    resolvePathFromBaseDir,
    resolvePathFromConfigFile,
} from "../shared/utils/config-path.utils";
import {
    type BootstrapLocalTasksResult,
    TASK_LOCAL_PACKAGE_CONFIG_FILENAME,
    TASK_RUNNER_ROOT_CONFIG_LEGACY_RELATIVE_PATH,
    TASK_RUNNER_ROOT_CONFIG_RELATIVE_PATH,
    type TaskLocalPackageConfig,
    type TaskRunnerRootConfig,
} from "./task-repo-context";

export interface BootstrapLocalTasksOptions {
    /** Absolute or cwd-relative path to root config; if omitted, tries default paths under `cwd` */
    rootConfigPath?: string;
    /** Base directory for resolving root config (default `process.cwd()`) */
    cwd?: string;
    /**
     * When set with an unchanged config fingerprint, skip full scan if this key is already
     * registered in {@link RepoManager}.
     */
    forTaskTypeKey?: string;
}

const EMPTY_RESULT: BootstrapLocalTasksResult = {
    status: "not_configured",
    registered: [],
    skipped: [],
    errors: [],
};

type BootstrapCacheState = {
    fingerprint: string | null;
    lastResult: BootstrapLocalTasksResult | null;
};

let bootstrapCache: BootstrapCacheState = { fingerprint: null, lastResult: null };
let bootstrapInFlight: Promise<BootstrapLocalTasksResult> | null = null;

function parseRootConfig(raw: unknown, configPath: string): TaskRunnerRootConfig {
    if (raw == null || typeof raw !== "object") {
        throw new Error(`Root config must export an object: ${configPath}`);
    }
    const record = raw as Record<string, unknown>;
    const taskDir = record.taskDir;
    if (typeof taskDir !== "string" || taskDir.trim() === "") {
        throw new Error(`Root config.taskDir must be a non-empty string: ${configPath}`);
    }
    return { taskDir: taskDir.trim() };
}

function parseTaskPackageConfig(raw: unknown, configPath: string): TaskLocalPackageConfig {
    if (raw == null || typeof raw !== "object") {
        throw new Error(`Task config must export an object: ${configPath}`);
    }
    const record = raw as Record<string, unknown>;
    const taskTypeKey = record.taskTypeKey;
    const entry = record.entry;

    if (typeof taskTypeKey !== "string" || taskTypeKey.trim() === "") {
        throw new Error(`taskTypeKey must be a non-empty string: ${configPath}`);
    }
    if (typeof entry !== "string" || entry.trim() === "") {
        throw new Error(`entry must be a non-empty string: ${configPath}`);
    }

    return {
        taskTypeKey: taskTypeKey.trim(),
        entry: entry.trim(),
        exportName: typeof record.exportName === "string" ? record.exportName : undefined,
        displayName: typeof record.displayName === "string" ? record.displayName : undefined,
        enabled: record.enabled !== false,
    };
}

function finalizeStatus(result: Omit<BootstrapLocalTasksResult, "status">): BootstrapLocalTasksResult["status"] {
    if (result.errors.length > 0 && result.registered.length === 0) {
        return "failed";
    }
    if (result.registered.length === 0) {
        return "empty";
    }
    return "loaded";
}

async function computeConfigFingerprint(rootConfigPath: string, tasksRoot: string): Promise<string> {
    const rootStat = await stat(rootConfigPath);
    const tasksRootStat = await stat(tasksRoot);
    const entries = await readdir(tasksRoot, { withFileTypes: true });
    const configMtims: number[] = [];

    for (const entry of entries) {
        if (!entry.isDirectory()) {
            continue;
        }
        const configPath = join(tasksRoot, entry.name, TASK_LOCAL_PACKAGE_CONFIG_FILENAME);
        if (await pathExists(configPath)) {
            const configStat = await stat(configPath);
            configMtims.push(configStat.mtimeMs);
        }
    }

    configMtims.sort((a, b) => a - b);
    return `${rootConfigPath}:${rootStat.mtimeMs}:${tasksRoot}:${tasksRootStat.mtimeMs}:${configMtims.join(",")}`;
}

function canUseCachedBootstrap(
    fingerprint: string,
    forTaskTypeKey: string | undefined,
): BootstrapLocalTasksResult | null {
    if (bootstrapCache.fingerprint !== fingerprint || bootstrapCache.lastResult == null) {
        return null;
    }

    if (forTaskTypeKey != null && forTaskTypeKey !== "") {
        if (!RepoManager.hasLocalRepo(forTaskTypeKey)) {
            return null;
        }
    }

    return { ...bootstrapCache.lastResult, cached: true };
}

async function runBootstrapScan(options: BootstrapLocalTasksOptions): Promise<BootstrapLocalTasksResult> {
    const cwd = options.cwd ?? process.cwd();

    const rootConfigPath = await resolveFirstExistingPath(cwd, {
        explicitPath: options.rootConfigPath,
        candidatePaths: [TASK_RUNNER_ROOT_CONFIG_RELATIVE_PATH, TASK_RUNNER_ROOT_CONFIG_LEGACY_RELATIVE_PATH],
    });
    if (rootConfigPath == null) {
        const result: BootstrapLocalTasksResult = {
            ...EMPTY_RESULT,
            message: `No root config (tried ${TASK_RUNNER_ROOT_CONFIG_RELATIVE_PATH}, ${TASK_RUNNER_ROOT_CONFIG_LEGACY_RELATIVE_PATH})`,
        };
        bootstrapCache = { fingerprint: null, lastResult: result };
        return result;
    }

    const result: Omit<BootstrapLocalTasksResult, "status"> = {
        registered: [],
        skipped: [],
        errors: [],
    };

    try {
        const rootRaw = await importJsConfigModule(rootConfigPath);
        const { taskDir } = parseRootConfig(rootRaw, rootConfigPath);

        const tasksRoot = resolvePathFromConfigFile(rootConfigPath, taskDir);
        if (!(await pathExists(tasksRoot))) {
            const failed: BootstrapLocalTasksResult = {
                status: "failed",
                message: `taskDir does not exist: ${tasksRoot}`,
                ...result,
            };
            bootstrapCache = { fingerprint: null, lastResult: failed };
            return failed;
        }

        const fingerprint = await computeConfigFingerprint(rootConfigPath, tasksRoot);
        const cached = canUseCachedBootstrap(fingerprint, options.forTaskTypeKey);
        if (cached != null) {
            return cached;
        }

        const entries = await readdir(tasksRoot, { withFileTypes: true });
        for (const entry of entries) {
            if (!entry.isDirectory()) {
                continue;
            }

            const taskPackageDir = join(tasksRoot, entry.name);
            const taskConfigPath = join(taskPackageDir, TASK_LOCAL_PACKAGE_CONFIG_FILENAME);

            if (!(await pathExists(taskConfigPath))) {
                result.skipped.push({ taskDir: taskPackageDir, reason: "missing task.config.js" });
                continue;
            }

            try {
                const taskRaw = await importJsConfigModule(taskConfigPath);
                const taskConfig = parseTaskPackageConfig(taskRaw, taskConfigPath);

                if (taskConfig.enabled === false) {
                    result.skipped.push({ taskDir: taskPackageDir, reason: "disabled in task.config.js" });
                    continue;
                }

                const entryPath = resolvePathFromBaseDir(taskPackageDir, taskConfig.entry);
                if (!(await pathExists(entryPath))) {
                    throw new Error(`entry module not found: ${entryPath}`);
                }

                RepoManager.registerLocalModule(taskConfig.taskTypeKey, entryPath, taskConfig.exportName);
                result.registered.push(taskConfig.taskTypeKey);
            } catch (error) {
                result.errors.push({
                    taskDir: taskPackageDir,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }

        const status = finalizeStatus(result);
        const finalResult: BootstrapLocalTasksResult = {
            status,
            message: status === "empty" ? `Root config loaded but no tasks registered under ${tasksRoot}` : undefined,
            ...result,
        };
        bootstrapCache = { fingerprint, lastResult: finalResult };
        return finalResult;
    } catch (error) {
        const failed: BootstrapLocalTasksResult = {
            status: "failed",
            message: error instanceof Error ? error.message : String(error),
            ...result,
        };
        bootstrapCache = { fingerprint: null, lastResult: failed };
        return failed;
    }
}

/**
 * Discover local task packages from config files and register them with {@link RepoManager}.
 *
 * **Never throws** for missing root config — returns `{ status: "not_configured" }`.
 *
 * Re-scans are skipped when the config fingerprint is unchanged (root config + taskDir mtimes).
 * Concurrent calls share a single in-flight scan (single-flight).
 *
 * Host app layout (recommended):
 * ```
 * ./config/task.config.js       → { taskDir: "../tasks" }
 * ./tasks/my-task/task.config.js
 * ./tasks/my-task/index.mjs
 * ```
 *
 * `taskDir` / `entry` paths are relative to **their config file's directory** (see config-file-relative-paths skill).
 *
 * Legacy fallback: `./task.config.js` at project root (`taskDir: "./tasks"`).
 */
export async function bootstrapLocalTasks(
    options: BootstrapLocalTasksOptions = {},
): Promise<BootstrapLocalTasksResult> {
    if (bootstrapInFlight != null) {
        return bootstrapInFlight;
    }

    bootstrapInFlight = runBootstrapScan(options).finally(() => {
        bootstrapInFlight = null;
    });
    return bootstrapInFlight;
}

/** Clear fingerprint cache (tests / hot-reload tooling). */
export function clearBootstrapLocalTasksCache(): void {
    bootstrapCache = { fingerprint: null, lastResult: null };
    bootstrapInFlight = null;
}

export function getRegisteredLocalTaskTypeKeys(): string[] {
    return RepoManager.getRegisteredLocalTaskTypeKeys();
}

export { TASK_RUNNER_ROOT_CONFIG_RELATIVE_PATH };
